import { useState, useMemo } from "react";
import {
  addDays,
  format,
  isSameDay,
  startOfDay,
  startOfWeek,
} from 'date-fns';
import { ru } from 'date-fns/locale';

export interface LessonSchedule {
  id: number;
  courseCode: string;
  subjectName: string;
  startTime: string; // Format "HH:mm"
  endTime: string; // Format "HH:mm"
  day: Date;
  cabinet: string;
  teacher: string;
  lessonId?: number; // ID реального урока для связи с данными
  courseId?: number; // ID курса
  classId?: number; // ID класса
}

interface AttendanceWeeklyScheduleProps {
  lessons?: LessonSchedule[];
  onLessonClick?: (lesson: LessonSchedule) => void;
}

// Mock данные для недельного расписания (должны приходить с API)
// В реальном приложении это будет приходить с бэкэнда
const getWeeklyScheduleLessons = (weekStart: Date): LessonSchedule[] => {
  // Вычисляем даты для текущей недели (понедельник - суббота)
  const monday = new Date(weekStart);
  const tuesday = new Date(monday);
  tuesday.setDate(monday.getDate() + 1);
  const wednesday = new Date(monday);
  wednesday.setDate(monday.getDate() + 2);
  const thursday = new Date(monday);
  thursday.setDate(monday.getDate() + 3);
  const friday = new Date(monday);
  friday.setDate(monday.getDate() + 4);

  return [
    {
      id: 1,
      courseCode: 'CEEN2217',
      subjectName: 'Элементы и устройства автоматики',
      startTime: '09:00',
      endTime: '10:00',
      day: new Date(monday),
      cabinet: 'Л 446',
      teacher: 'Свамбаева А.С.',
    },
    {
      id: 2,
      courseCode: 'CEEN2217',
      subjectName: 'Элементы и устройства автоматики',
      startTime: '10:00',
      endTime: '11:00',
      day: new Date(monday),
      cabinet: 'Л 446',
      teacher: 'Свамбаева А.С.',
    },
    {
      id: 3,
      courseCode: 'CEEN2217',
      subjectName: 'Элементы и устройства автоматики',
      startTime: '11:00',
      endTime: '12:00',
      day: new Date(tuesday),
      cabinet: 'Л 446',
      teacher: 'Свамбаева А.С.',
    },
    {
      id: 4,
      courseCode: 'CEEN2217',
      subjectName: 'Элементы и устройства автоматики',
      startTime: '09:00',
      endTime: '10:00',
      day: new Date(wednesday),
      cabinet: 'Л 446',
      teacher: 'Свамбаева А.С.',
    },
    {
      id: 5,
      courseCode: 'CEEN2217',
      subjectName: 'Элементы и устройства автоматики',
      startTime: '09:00',
      endTime: '10:00',
      day: new Date(thursday),
      cabinet: 'Л 446',
      teacher: 'Свамбаева А.С.',
    },
    {
      id: 6,
      courseCode: 'CEEN2217',
      subjectName: 'Элементы и устройства автоматики',
      startTime: '11:00',
      endTime: '12:00',
      day: new Date(friday),
      cabinet: 'Л 446',
      teacher: 'Свамбаева А.С.',
    },
  ];
};

type GridCell =
  | null
  | 'skip'
  | {
      lesson: LessonSchedule;
      rowSpan: number;
    };

/** A helper to build a grid-like array from the list of lessons. */
function buildGrid(
  lessons: LessonSchedule[],
  days: Date[],
  timeSlots: string[]
): GridCell[][] {
  const rows = timeSlots.length;
  const cols = days.length;
  const grid = Array.from({ length: rows }, () => Array(cols).fill(null));

  for (let d = 0; d < cols; d++) {
    for (let t = 0; t < rows; t++) {
      if (grid[t][d] !== null) continue;

      // Find lesson that starts exactly at this day/time
      const day = days[d];
      const slotTime = timeSlots[t];

      const found = lessons.find((lesson) => {
        // Compare day ignoring time
        if (!isSameDay(startOfDay(lesson.day), startOfDay(day))) return false;
        return lesson.startTime === slotTime;
      });

      if (found) {
        // Calculate how many 30-min slots this lesson spans
        const [sh, sm] = found.startTime.split(':').map(Number);
        const [eh, em] = found.endTime.split(':').map(Number);
        const duration = eh * 60 + em - (sh * 60 + sm); // in minutes
        const slotsCount = Math.max(1, Math.ceil(duration / 30));

        grid[t][d] = { lesson: found, rowSpan: slotsCount };

        // Mark subsequent rows as 'skip'
        for (let skip = 1; skip < slotsCount; skip++) {
          if (t + skip < rows) {
            grid[t + skip][d] = 'skip';
          }
        }
      }
    }
  }
  return grid;
}

export default function ScheduleOfWeek({
  lessons: propLessons = [],
  onLessonClick
}: AttendanceWeeklyScheduleProps) {
  const [currentWeek, setCurrentWeek] = useState(new Date());

  /** Generate days for the current week (Monday to Saturday) */
  const startOfWeekDate = startOfWeek(currentWeek, { weekStartsOn: 1 });
  const days = Array.from({ length: 6 }, (_, i) => addDays(startOfWeekDate, i)); // Only Mon-Sat

  /** Generate time slots from 08:00 to 17:00 by 30 minutes */
  const timeSlots = useMemo(() => {
    const slots: string[] = [];
    for (let hour = 8; hour < 18; hour++) {
      const hh = hour.toString().padStart(2, '0');
      slots.push(`${hh}:00`);
      slots.push(`${hh}:30`);
    }
    return slots;
  }, []);

  /** Get lessons for current week - use propLessons if provided, otherwise use mock data */
  const lessons = useMemo(() => {
    if (propLessons.length > 0) {
      // Если переданы уроки, используем их, но фильтруем по текущей неделе
      return propLessons.filter(lesson => {
        const lessonDay = startOfDay(lesson.day);
        return days.some(day => isSameDay(lessonDay, day));
      });
    }
    // Иначе используем мок-данные
    return getWeeklyScheduleLessons(startOfWeekDate);
  }, [propLessons, startOfWeekDate, days]);

  /** Build a grid from lessons for the table to render */
  const grid = useMemo(() => {
    return buildGrid(lessons, days, timeSlots);
  }, [lessons, days, timeSlots]);

  /** Move to previous/next week */
  const handlePrevWeek = () => setCurrentWeek((w) => addDays(w, -7));
  const handleNextWeek = () => setCurrentWeek((w) => addDays(w, 7));
  const handleToday = () =>
    setCurrentWeek(startOfWeek(new Date(), { weekStartsOn: 1 }));

  return (
    <div className="bg-white rounded-lg flex-1 p-4 font-['SF Pro Text'] relative overflow-y-auto">
      {/* Main Table */}
      <div className="overflow-auto">
        <table className="w-full border-collapse table-fixed text-black">
          <thead>
            <tr>
              <th className="w-12 border border-gray-200" />
              {days.map((day, dIndex) => (
                <th
                  key={dIndex}
                  className="border border-gray-200 bg-gray-100 py-4 w-[125px]"
                >
                  <div className="flex items-center px-2 justify-center">
                    <span className="text-gray-700 font-medium">
                      {format(day, 'EEE', { locale: ru }).slice(0, 2)}
                    </span>
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {timeSlots.map((slot, rowIndex) => (
              <tr
                key={rowIndex}
                className={`${slot.endsWith(':30')
                  ? 'border-none '
                  : 'border-t border-gray-200'
                  }`}
                style={{ height: '40px' }}
              >
                {/* Time label on left */}
                <td
                  className="relative text-center border-r border-gray-200"
                  style={{ position: 'relative' }}
                >
                  {slot.endsWith(':00') && (
                    <div
                      className="absolute left-1/2 transform -translate-x-1/2"
                      style={{
                        top: '-10px',
                        background: 'white',
                      }}
                    >
                      {slot}
                    </div>
                  )}
                </td>

                {/* Days columns */}
                {days.map((day, colIndex) => {
                  const cell = grid[rowIndex][colIndex];
                  if (cell === 'skip') {
                    return null; // skip cell
                  }
                  if (cell && cell.lesson) {
                    const { lesson, rowSpan } = cell;
                    return (
                      <td
                        key={colIndex}
                        rowSpan={rowSpan}
                        className="relative text-center border-r border-gray-200 p-0"
                      >
                        <div
                          className={`m-0.5 absolute inset-0 rounded text-left flex flex-col justify-start items-start px-2 py-1 bg-valley-main/20 ${onLessonClick ? 'cursor-pointer hover:bg-valley-main/30' : ''}`}
                          onClick={() => onLessonClick?.(lesson)}
                        >
                          <div className="text-[11px] font-semibold text-valley-gray leading-tight">
                            {lesson.courseCode}
                          </div>
                          <div className="text-[10px] text-gray-600 leading-tight mt-0.5">
                            {lesson.startTime} - {lesson.endTime}
                          </div>
                          <div className="text-[11px] font-medium text-gray-800 leading-tight mt-0.5">
                            {lesson.subjectName}
                          </div>
                          <div className="text-[10px] text-gray-600 leading-tight mt-0.5">
                            {lesson.cabinet}
                          </div>
                          <div className="text-[10px] text-gray-600 leading-tight">
                            {lesson.teacher}
                          </div>
                        </div>
                      </td>
                    );
                  }

                  return (
                    <td
                      key={colIndex}
                      className="relative border-r border-gray-200 p-0"
                    ></td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

